import { XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import type { DetailedHTMLProps, InputHTMLAttributes } from 'react';
import { useRef, useState } from 'react';
import { useCallback, useEffect } from 'react';
import { navigate } from 'vike/client/router';

import type { Optional } from '@/renderer/types';
import {
  type AlertId,
  type IRasAlertAddData,
  dispatchRemoveAlert,
  eventTarget,
} from '@/renderer/utils/alerts';
import { useMediaQuery } from '@/renderer/utils/hooks/useMediaQuery';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import { IS_SERVER } from '@/renderer/utils/ssr';
import { useIdleContext } from '@/renderer/utils/useIdle';

import { twMerge } from 'tailwind-merge';
import { Logo } from '../Logo';
import Avatar from './Avatar';

export default Alert;

export { Alerts };

// const DEFAULT_DURATION = 10000000;
const DEFAULT_DURATION = 8000;
// Cap how many toasts stack on-screen at once. Five was too many, especially on a
// phone, so the effective cap is responsive: fewer on mobile than desktop. Permanent
// toasts (e.g. the client-update prompt) are exempt and always render (see Alerts()).
const MAX_ALERTS_DESKTOP = 3;
const MAX_ALERTS_MOBILE = 2;

function Alert<
  P extends Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'id'> &
    Omit<Optional<IRasAlertAddData, 'id'>, 'message'>,
>({
  actions,
  // icon,
  children,
  id,
  status,
  uFirst,
  uLast,
  uId,
  uRole,
  uAvatarUrl,
  href,
  permanent,
}: P) {
  const statusColor = status || 'info';
  // `update` is the client-out-of-date ("a new version is ready") prompt. It keeps a
  // clean, neutral card (NOT reused `success` green) and carries its emphasis through
  // an integrated, brand-styled inline action button instead of the sectioned-off
  // action column (#1631).
  const isUpdate = statusColor === 'update';
  const bg =
    statusColor === 'error'
      ? 'bg-[color-mix(in_srgb,var(--light-destructive)_5%,var(--color-white))] dark:bg-[color-mix(in_srgb,var(--dark-destructive)_5%,var(--color-zinc-900))]'
      : statusColor === 'warning'
        ? 'bg-[color-mix(in_srgb,var(--light-warning)_5%,var(--color-white))] dark:bg-[color-mix(in_srgb,var(--dark-warning)_5%,var(--color-zinc-900))]'
        : statusColor === 'success'
          ? 'bg-[color-mix(in_srgb,var(--light-success)_5%,var(--color-white))] dark:bg-[color-mix(in_srgb,var(--dark-success)_5%,var(--color-zinc-900))]'
          : '';

  const idle = useIdleContext();

  useUpdateEffect(() => {
    if (!idle.inactive && location.pathname === href && id) {
      dispatchRemoveAlert(id);
    }
  }, [idle.inactive]);

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!href) {
        return;
      }

      if ((event.target as HTMLElement).matches('button, a, button *, a *')) {
        return; // don't handle href clicks if the user clicked a button or anchor inside the alert
      }

      navigate(href);
      if (id && !permanent) {
        dispatchRemoveAlert(id);
      }
    },
    [href, id, permanent],
  );

  const showAvatar = !!uFirst && !!uLast && !!uId;
  // The update ("new version available") toast is intentionally text + button only,
  // so suppress the RAS logo there while leaving avatar/logo behavior untouched for
  // every other toast.
  const showLogo = !showAvatar && !isUpdate;

  return (
    <>
      <div
        className="flex w-full flex-col items-center space-y-4 sm:items-end sm:pl-10"
        onClick={onClick}
      >
        <div
          className={twMerge(
            'pointer-events-auto flex w-full max-w-sm divide-x divide-zinc-200 rounded-sm border border-zinc-900/5 bg-white shadow-lg dark:divide-zinc-700 dark:border-white/10 dark:bg-zinc-900 dark:shadow-none',
            bg,
          )}
        >
          <div className="w-0 flex-1 p-4">
            <div className="flex items-start">
              {(showAvatar || showLogo) && (
                <div className="shrink-0 pt-0.5">
                  {showAvatar && (
                    <Avatar
                      user={{ firstName: uFirst, lastName: uLast, id: uId, avatarUrl: uAvatarUrl }}
                      isAdmin={uRole === 'admin' || uRole === 'staff'}
                    />
                  )}
                  {showLogo && <Logo className="h-10 w-10 select-none rounded-sm" />}
                </div>
              )}
              <div className={clsx('w-0 flex-1 pt-0.5', (showAvatar || showLogo) && 'ml-3')}>
                {children}
                {isUpdate && !!actions?.length && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {actions.map((action, i) => {
                      const Component = action.href ? ('a' as const) : ('button' as const);

                      return (
                        <Component
                          key={action.href || i}
                          href={action.href}
                          type="button"
                          className="inline-flex items-center justify-center rounded-sm bg-emerald-600 px-3 py-1.5 text-center font-semibold text-sm text-white shadow-xs transition-colors hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-500"
                          onClick={action.onClick}
                        >
                          {action.text}
                        </Component>
                      );
                    })}
                  </div>
                )}
              </div>
              {!!id && !permanent && !actions?.length && (
                <div className="ml-4 flex shrink-0">
                  <button
                    type="button"
                    className="inline-flex rounded-md bg-white text-zinc-400 hover:text-zinc-500 focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:bg-zinc-900"
                    onClick={() => dispatchRemoveAlert(id)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          </div>
          {!isUpdate && (
            <div className="flex max-w-[33%] shrink whitespace-break-spaces text-wrap">
              <div className="flex flex-col divide-y divide-zinc-200 dark:divide-zinc-700">
                <div className="flex h-0 flex-1">
                  {actions?.map((action, i) => {
                    const showDismiss = !!id && !permanent;
                    const Component = action.href ? ('a' as const) : ('button' as const);

                    return (
                      <Component
                        key={action.href || i}
                        href={action.href}
                        type="button"
                        className={clsx(
                          'flex w-full items-center justify-center rounded-none border border-transparent px-3 py-2 text-center font-medium text-emerald-500 text-sm hover:text-emerald-400 focus:z-10 focus:outline-hidden focus:ring-2 focus:ring-emerald-500',
                          i === 0 && 'rounded-tr-lg',
                          !showDismiss && i === actions.length - 1 && 'rounded-br-lg',
                        )}
                        onClick={action.onClick}
                        // onClick={() => {
                        //   setShow(false);
                        // }}
                      >
                        {action.text}
                      </Component>
                    );
                  })}
                </div>
                {!!id && !permanent && actions?.length && (
                  <div className="flex h-0 flex-1">
                    <button
                      type="button"
                      className="flex w-full items-center justify-center rounded-none rounded-br-lg border border-transparent px-3 py-2 font-medium text-sm text-zinc-700 hover:text-zinc-500 focus:outline-hidden focus:ring-2 focus:ring-emerald-500"
                      onClick={() => dispatchRemoveAlert(id)}
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Alerts() {
  const ids = useRef<Set<AlertId>>(new Set());
  const [alerts, setAlerts] = useState<{ [i: AlertId]: IRasAlertAddData }>({});
  // const alerts = AlertsStore.useState(s => s.alerts);

  const addCallback = useCallback((event: CustomEvent<IRasAlertAddData>) => {
    const {
      actions,
      icon,
      id,
      message,
      status,
      submessage,
      pre,
      duration,
      href,
      permanent,
      uFirst,
      uLast,
      uId,
      uRole,
    } = event.detail;

    setAlerts((oldAlerts) => {
      let _timeout;

      if (oldAlerts[id]) {
        clearTimeout(oldAlerts[id]._timeout);
        ids.current.delete(id); // we will re-add it at the end of the stack
      }

      ids.current.add(id);

      if (!IS_SERVER && (duration === undefined || duration) && !permanent) {
        _timeout = setTimeout(() => {
          dispatchRemoveAlert(id);
        }, duration || DEFAULT_DURATION);
      }

      return {
        ...oldAlerts,
        [id]: {
          actions,
          icon,
          id,
          message,
          status,
          submessage,
          pre,
          duration,
          href,
          permanent,
          uFirst,
          uLast,
          uId,
          uRole,
          _timeout,
        },
      };
    });
  }, []);

  const removeCallback = useCallback((event: CustomEvent<AlertId>) => {
    const id = event.detail;

    if (ids.current.has(id)) {
      setAlerts((oldAlerts) => {
        if (oldAlerts[id]) {
          const { [id]: old, ...remainingAlerts } = oldAlerts;

          clearTimeout(old._timeout);

          ids.current.delete(id);

          return remainingAlerts;
        }

        return oldAlerts;
      });
    }
  }, []);

  useEffect(() => {
    // Only happens client-side
    eventTarget.addEventListener('rasalertadd', addCallback, false);
    eventTarget.addEventListener('rasalertremove', removeCallback, false);

    return () => {
      eventTarget.removeEventListener('rasalertadd', addCallback, false);
      eventTarget.removeEventListener('rasalertremove', removeCallback, false);
    };
  }, []);

  // Matches this component's own `sm:` breakpoint (640px) — desktop stacks more toasts
  // than a phone. Bound to matchMedia so the cap re-evaluates on resize/rotate.
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const cap = isDesktop ? MAX_ALERTS_DESKTOP : MAX_ALERTS_MOBILE;

  // Apply the cap to transient toasts only: permanent alerts (e.g. the client-update
  // "new version available" prompt) always render and must never be bumped off-screen
  // by a flurry of newer transient toasts. Fill the remaining slots with the NEWEST
  // transient alerts and render newest-first (#1651). If permanent alerts alone exceed
  // the cap, still render all of them.
  const ordered = [...ids.current]; // oldest -> newest (Set insertion order)
  const permanentIds = ordered.filter((id) => alerts[id]?.permanent);
  const transientIds = ordered.filter((id) => !alerts[id]?.permanent);
  const transientSlots = Math.max(cap - permanentIds.length, 0);
  // slice(-0) would return the whole array, so guard the empty-slots case explicitly.
  const visibleTransient = transientSlots > 0 ? transientIds.slice(-transientSlots) : [];
  const visibleIds = new Set([...permanentIds, ...visibleTransient]);
  const renderIds = ordered.filter((id) => visibleIds.has(id)).reverse();

  return (
    <div
      aria-live="assertive"
      // Fixed/portaled to the viewport, so it escapes the native shell's content
      // inset — pad by the safe-area insets so toasts (e.g. "Auto-grade applied")
      // clear the notch/status bar and home indicator on native (#1601).
      className="pointer-events-none fixed inset-0 z-50 flex flex-col items-end space-y-2 px-4 pt-[calc(1.5rem+var(--safe-top))] pb-[calc(1.5rem+var(--safe-bottom))] sm:items-start sm:px-6"
    >
      {/* <div className="fixed right-1 top-1 z-50 flex max-w-(--breakpoint-sm) flex-col space-y-2"> */}
      {renderIds.map((key) => {
        const alert = alerts[key];

        return (
          !!alert && (
            <Alert
              id={alert.id}
              key={alert.id}
              status={alert.status}
              icon={alert.icon}
              actions={alert.actions}
              href={alert.href}
              permanent={alert.permanent}
              uFirst={alert.uFirst}
              uLast={alert.uLast}
              uId={alert.uId}
              uRole={alert.uRole}
            >
              <div>
                {!!alert.submessage && (
                  <div
                    className={twMerge(
                      'line-clamp-4 break-words font-medium text-sm text-zinc-900 dark:text-zinc-500',
                      // Update toast keeps the title as the bright/emphasized line in dark
                      // mode too (the shared default dims submessage in dark).
                      alert.status === 'update' && 'dark:text-white',
                    )}
                  >
                    {alert.submessage}
                  </div>
                )}
                {!!alert.pre && (
                  <code className="line-clamp-4 break-all font-normal text-xs text-zinc-500 dark:text-white">
                    {alert.pre}
                  </code>
                )}
                <div
                  className={twMerge(
                    'mt-1 line-clamp-4 break-words text-sm text-zinc-500 dark:text-white',
                    // ...and the supporting sentence stays muted in dark mode, so the
                    // bold-title-over-lighter-line hierarchy holds in both themes.
                    alert.status === 'update' && 'dark:text-zinc-400',
                  )}
                >
                  {alert.message}
                </div>
              </div>
            </Alert>
          )
        );
      })}
    </div>
  );
}
